import { useCallback, useEffect, useState } from 'react';
import { api, type OperatorDto } from '../lib/api';
import { stampDate } from '../lib/format';
import { useT, type MessageKey } from '../lib/i18n';

/**
 * Who may sign in, and what each of them may do.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS SCREEN EXISTS
 * ---------------------------------------------------------------------------
 *
 * There was no second account. `/api/setup` makes the first operator and then
 * refuses forever, so a park was one person sharing one login — and the change
 * log records who did what, which is worth nothing when everyone is the same
 * who. The first thing a real park asked for was a reception account that can
 * see prices without being able to change them.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE CHECKBOXES ARE, AND ARE NOT
 * ---------------------------------------------------------------------------
 *
 * They are the same capability names the server enforces, offered as a list the
 * server itself supplies — there is no second copy here to drift when one is
 * added. Hiding a control elsewhere in this panel is a courtesy; THIS is the
 * thing that actually decides, because the server reads these on every request.
 *
 * Roles are deliberately absent. A named role is a second vocabulary that has
 * to be kept in step with the first, and the park has three people: naming the
 * capabilities directly is shorter to read and impossible to get subtly wrong.
 * The presets below are a shortcut for typing, not a model.
 */
export function Accounts({ run, busy, meId }: {
  /**
   * The dashboard's request runner: shows a spinner, reports failures.
   *
   * Returns `null` on failure, so every call site here has to check rather than
   * assume — a create that 409'd must not close the form and lose what was typed.
   */
  run: <T>(work: () => Promise<T>, done?: string | ((out: T) => string)) => Promise<T | null>;
  busy?: boolean;
  /**
   * The signed-in account, so its own row cannot end its own access.
   *
   * The server refuses it either way; this stops the controls being offered.
   * Disabling yourself or removing your own permissions is never what somebody
   * meant — it is a row misread in a list where every row looks alike.
   */
  meId?: string | null;
}) {
  const t = useT();
  const [operators, setOperators] = useState<OperatorDto[] | null>(null);
  const [available, setAvailable] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    login: string; label: string; password: string; capabilities: string[];
  } | null>(null);

  const load = useCallback(async () => {
    const out = await api.operators();
    setOperators(out.operators);
    setAvailable(out.capabilities);
    return out;
  }, []);

  useEffect(() => { void run(load); }, [run, load]);

  /**
   * What reception needs, as one button.
   *
   * Taken from what the park actually asked for: see the prices without
   * changing them, read and answer the enquiries, adjust occupancy. It fills
   * the form rather than defining a role, so it can be adjusted before saving
   * and does not become a second thing to maintain.
   *
   * `inquiry.write` is here because without it the queue never clears —
   * reception could read an enquiry and never tick it off, and "new" would
   * grow forever. It grants exactly one route, the status change; nothing in
   * the system deletes or edits an enquiry, for anyone.
   */
  const RECEPTION = ['catalogue.read', 'inquiry.read', 'inquiry.write', 'block.write'];

  const startNew = () => {
    setProblem(null);
    setDraft({ login: '', label: '', password: '', capabilities: [...RECEPTION] });
  };

  const toggle = (capability: string) => setDraft((d) => (d ? {
    ...d,
    capabilities: d.capabilities.includes(capability)
      ? d.capabilities.filter((c) => c !== capability)
      : [...d.capabilities, capability],
  } : d));

  const save = async () => {
    if (!draft) return;
    setProblem(null);
    // Checked here as well as at the API, because a 400 on a submitted form is
    // a worse way to learn the password was short than a sentence beside it.
    if (draft.login.trim().length < 3) { setProblem(t('accounts.badLogin')); return; }
    if (draft.password.length < 12) { setProblem(t('accounts.badPassword')); return; }
    const made = await run(() => api.createOperator({
      login: draft.login.trim(),
      password: draft.password,
      label: draft.label.trim() || draft.login.trim(),
      capabilities: draft.capabilities,
    }), t('accounts.created'));
    if (made) { setDraft(null); await run(load); }
  };

  const setCapabilities = async (o: OperatorDto, capabilities: string[]) => {
    await run(() => api.updateOperator(o.id, { capabilities }), t('accounts.saved'));
    await run(load);
  };

  const setDisabled = async (o: OperatorDto, disabled: boolean) => {
    await run(() => api.updateOperator(o.id, { disabled }), t('accounts.saved'));
    await run(load);
  };

  if (!operators) {
    return <section className="panel"><p className="note">{t('accounts.loading')}</p></section>;
  }

  return (
    <section className="panel">
      <div className="panel-hd">
        <h2>{t('accounts.heading')}</h2>
        <span className="grow" />
        <button type="button" className="btn" onClick={startNew} disabled={busy}>
          {t('accounts.add')}
        </button>
      </div>
      <div className="panel-bd">
        <p className="note">{t('accounts.note')}</p>

        {problem && <div className="banner bad" role="alert">{problem}</div>}

        {draft && (
          <fieldset className="card">
            <legend>{t('accounts.newHeading')}</legend>
            <div className="row">
              <label>
                {t('accounts.login')}
                <input
                  value={draft.login} autoComplete="off" maxLength={60}
                  onChange={(e) => setDraft({ ...draft, login: e.target.value })}
                />
              </label>
              <label>
                {t('accounts.label')}
                <input
                  value={draft.label} maxLength={80}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                />
              </label>
            </div>
            <div className="row">
              <label className="wide">
                {t('accounts.password')}
                <input
                  type="text" value={draft.password} autoComplete="new-password"
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                />
              </label>
            </div>
            {/* Shown, not masked. Somebody is creating this for a colleague and
                has to read it out; a dot-filled field they cannot check is how
                a password gets written on a sticky note instead. */}
            <p className="note">{t('accounts.passwordNote')}</p>

            <h3 className="eyebrow">{t('accounts.mayDo')}</h3>
            <div className="row">
              <button
                type="button" className="btn"
                onClick={() => setDraft({ ...draft, capabilities: [...RECEPTION] })}
              >
                {t('accounts.presetReception')}
              </button>
            </div>
            <ul className="caps">
              {available.map((c) => (
                <li key={c}>
                  <label>
                    <input
                      type="checkbox"
                      checked={draft.capabilities.includes(c)}
                      onChange={() => toggle(c)}
                    />
                    <code>{c}</code>
                    <span className="note">{t(capabilityKey(c))}</span>
                  </label>
                </li>
              ))}
            </ul>

            <div className="row">
              <button type="button" className="btn primary" onClick={save} disabled={busy}>
                {t('accounts.create')}
              </button>
              <button type="button" className="btn" onClick={() => setDraft(null)}>
                {t('accounts.cancel')}
              </button>
            </div>
          </fieldset>
        )}

        <table>
          <thead>
            <tr>
              <th>{t('accounts.login')}</th>
              <th>{t('accounts.mayDo')}</th>
              <th>{t('accounts.state')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {operators.map((o) => (
              <tr key={o.id} className={o.disabledAt ? 'muted' : undefined}>
                <td>
                  <strong>{o.login}</strong>
                  {o.label && o.label !== o.login && <div className="note">{o.label}</div>}
                </td>
                <td>
                  <ul className="caps compact">
                    {available.map((c) => (
                      <li key={c}>
                        <label>
                          <input
                            type="checkbox"
                            checked={o.capabilities.includes(c)}
                            disabled={busy || Boolean(o.disabledAt) || o.id === meId}
                            onChange={() => setCapabilities(
                              o,
                              o.capabilities.includes(c)
                                ? o.capabilities.filter((x) => x !== c)
                                : [...o.capabilities, c],
                            )}
                          />
                          <code>{c}</code>
                        </label>
                      </li>
                    ))}
                  </ul>
                </td>
                <td>
                  {o.disabledAt
                    ? <span className="note">{t('accounts.disabledOn', { when: stampDate(o.disabledAt) })}</span>
                    : <span className="note">{t('accounts.active')}</span>}
                </td>
                <td>
                  {o.id === meId
                    ? <span className="note">{t('accounts.you')}</span>
                    : (
                      <button
                        type="button" className="btn" disabled={busy}
                        onClick={() => setDisabled(o, !o.disabledAt)}
                      >
                        {o.disabledAt ? t('accounts.enable') : t('accounts.disable')}
                      </button>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Disabling rather than deleting, because the change log names its
            author and removing the row would take that name with it. */}
        <p className="note">{t('accounts.disableNote')}</p>
      </div>
    </section>
  );
}

/** The sentence explaining one capability, or a fallback naming it. */
function capabilityKey(capability: string): MessageKey {
  return `capability.${capability}` as MessageKey;
}
